/**
 * @file Overlay Factory
 *
 * @author christian.sany@notch-interactive.com
 */

// Imports
import pubsub from '../libs/pubsub';
import observer from '../libs/observer';

const defaults = {
    bodyDisabledScrollingClass: 'body--noscroll',
    hookOpenTriggers: '[data-overlay]',
    hookOverlays: '[data-overlay-target]',
    hookCloseTriggers: '[data-overlay-closer]',
    hookBoundAttr: 'data-overlay-bound',
    triggerIdAttr: 'data-overlay',
    overlayIdAttr: 'data-overlay-target',
    typeAttr: 'data-overlay-type',
    overlayActiveClass: 'is-active',
    type: 'overlay',
    overlayId: '',
    ignoreOverflowAttr: 'data-ignore-overflow',
    boundAttr: 'data-ov-bound',
    insertEndOfBody: false
};

// TODO: Refactor this module to use delegates instead of adding eventhandlers to everything

/**
 * Overlay Factory
 *
 * @param {object} options - Override default settings with options object.
 * @return {object} instance
 */
export default (options) => {
    const instance = {};

    // Private variables
    let settings = Object.assign({}, defaults, options),
        activeOverlay,
        scrollTop = null,
        openTriggers,
        optionIgnoreOverflow = false,
        overlays;

    // Private methods
    const bindEvents = () => {
            if (openTriggers.length) {
                openTriggers.forEach((el) => {

                    // Prevent Element from binding multiple times
                    if (!el.hasAttribute(settings.hookBoundAttr)) {

                        // Set the bound Attribute
                        el.setAttribute(settings.boundAttr, '');
                        el.setAttribute(settings.hookBoundAttr, '');
                        el.addEventListener('click', () => openTriggerHandler(el));
                    }
                });
            }

            if (overlays.length) {
                overlays.forEach((el) => {
                    let closeTriggers = el.querySelectorAll(settings.hookCloseTriggers);

                    // Set the bound Attribute
                    el.setAttribute(settings.boundAttr, '');

                    if (closeTriggers.length) {
                        closeTriggers.forEach((el) => {

                            // Prevent Element from binding multiple times
                            if (!el.hasAttribute(settings.hookBoundAttr)) {

                                // Set the bound Attribute
                                el.setAttribute(settings.boundAttr, '');
                                el.setAttribute(settings.hookBoundAttr, '');
                                el.addEventListener('click', closeTriggerHandler);
                            }
                        });
                    }
                });
            }
        },

        unbindEvents = () => {
            if (openTriggers.length) {
                openTriggers.forEach((el) => {
                    el.removeAttribute(settings.boundAttr);
                    el.removeAttribute(settings.hookBoundAttr);
                    el.removeEventListener('click', openTriggerHandler);
                });
            }

            if (overlays.length) {
                overlays.forEach((el) => {
                    let closeTriggers = el.querySelectorAll(settings.hookCloseTriggers);

                    el.removeAttribute(settings.boundAttr);

                    if (closeTriggers.length) {
                        closeTriggers.forEach((el) => {
                            el.removeAttribute(settings.boundAttr);
                            el.removeAttribute(settings.hookBoundAttr);
                            el.removeEventListener('click', closeTriggerHandler);
                        });
                    }
                });
            }
        },

        openTriggerHandler = (el) => {
            openOverlay(el.getAttribute(settings.triggerIdAttr));
        },

        closeTriggerHandler = () => {
            closeOverlay();
        },

        overlayHasOverflow = () => {
            return activeOverlay.offsetHeight < activeOverlay.scrollHeight || activeOverlay.offsetWidth < activeOverlay.scrollWidth;
        },

        // Prevents the parent from scrolling in IOS
        preventScrollBubble = () => {
            let height = activeOverlay.offsetHeight,
                scrollHeight = activeOverlay.querySelector('.overlay__inner').offsetHeight;

            if (activeOverlay.scrollTop === 0) {
                activeOverlay.scrollTop = 1;
            } else if (scrollHeight === activeOverlay.scrollTop + height) {
                activeOverlay.scrollTop -= 1;
            }
        },

        scrollHandler = () => {

            // Check if the overlay has an overflow
            if (overlayHasOverflow()) {
                preventScrollBubble();
            }
        },

        touchHandler = () => {

            // Check if the overlay has an overflow
            if (overlayHasOverflow()) {
                preventScrollBubble();
            } else {

                let callbackMove,
                    callbackEnd;

                // Prevent bodyscrolling when no overflow is found on the overlay
                activeOverlay.addEventListener('touchmove', callbackMove = (event) => {
                    event.preventDefault();
                });

                activeOverlay.addEventListener('touchend', callbackEnd = () => {

                    if (callbackMove) {
                        activeOverlay.removeEventListener('touchmove', callbackMove);
                    }

                    activeOverlay.removeEventListener('touchend', callbackEnd);
                });
            }
        },

        keyHandler = (e) => {
            if (e.keyCode === 27) {
                closeOverlay();
            }
        },

        openOverlay = (id) => {
            if (!id) {
                throw new Error('ID must be given when trying to open an overlay');
            }

            if(scrollTop===null) {

                // store scrolltop position
                scrollTop = window.pageYOffset;
            }

            activeOverlay = overlays.filter((overlay) => overlay.getAttribute(settings.overlayIdAttr) === id)[0];

            if (!activeOverlay) {
                throw new Error('Overlay was not found');
            }

            // Check if overlay is not at the end of the body and it should be put there
            if (activeOverlay.parentNode.tagName !== 'BODY' && settings.insertEndOfBody) {
                document.body.appendChild(activeOverlay);
            }

            // Get Options
            optionIgnoreOverflow = activeOverlay.hasAttribute(settings.ignoreOverflowAttr);

            activeOverlay.style.display = 'block';

            setTimeout(() => {

                // Create possibility for other modules to listen when an overlay opened
                // First parameter received in the callback is the opened overlay
                pubsub.trigger('overlay.opened', activeOverlay);

                instance.trigger('opened', activeOverlay);

                activeOverlay.classList.add(settings.overlayActiveClass);

                // Disble bodyscrolling
                document.documentElement.classList.add(settings.bodyDisabledScrollingClass);

                // Fixes issue on IOS when scrolling initially up on opening the overlay
                if (overlayHasOverflow()) {
                    activeOverlay.scrollTop = 1;
                }

                // Because these events are currently only used to check overflow, we can simply not bind them.
                if (!optionIgnoreOverflow) {
                    activeOverlay.addEventListener('touchstart', touchHandler);
                    activeOverlay.addEventListener('scroll', scrollHandler);
                }

                window.addEventListener('keydown', keyHandler);

            }, 20);
        },

        closeOverlay = () => {
            if (activeOverlay) {

                // Self removing eventListener
                activeOverlay.addEventListener('transitionend', function callback() {
                    activeOverlay.removeEventListener('transitionend', callback);

                    // Create possibility for other modules to listen when an overlay closed
                    // First parameter received in the callback is the closed overlay
                    pubsub.trigger('overlay.closed', activeOverlay);

                    instance.trigger('closed', activeOverlay);

                    activeOverlay.style.display = 'none';

                    // Unset activeOverlay
                    activeOverlay = null;
                });

                // Enable bodyscrolling
                document.documentElement.classList.remove(settings.bodyDisabledScrollingClass);

                activeOverlay.removeEventListener('touchstart', scrollHandler);
                activeOverlay.removeEventListener('scroll', scrollHandler);
                window.removeEventListener('keydown', keyHandler);

                window.requestAnimationFrame(function() {
                    activeOverlay.classList.remove(settings.overlayActiveClass);

                    window.requestAnimationFrame(function() {
                        window.scrollTo(0, scrollTop);
                        scrollTop = null;
                    });
                });
            }
        };

    // Add composites
    Object.assign(instance, observer());

    // Public methods

    /**
     * Destroy this module
     */
    instance.destroy = () => {

        // Close overlay if currently open
        closeOverlay();

        // Unbind all eventhandlers
        unbindEvents();
    };

    /**
     * Close the currently active overlay
     */
    instance.close = () => {
        closeOverlay();
    };

    // Load the dom elements
    if (settings.overlayId !== '') {
        openTriggers = document.querySelectorAll(settings.hookOpenTriggers + '[' + settings.triggerIdAttr + '="' + settings.overlayId + '"][' + settings.typeAttr + '="' + settings.type + '"]:not([' + settings.boundAttr + '])');
        overlays = [...document.querySelectorAll(settings.hookOverlays + '[' + settings.overlayIdAttr + '="' + settings.overlayId + '"][' + settings.typeAttr + '="' + settings.type + '"]:not([' + settings.boundAttr + '])')];
    } else {
        openTriggers = document.querySelectorAll(settings.hookOpenTriggers + '[' + settings.typeAttr + '="' + settings.type + '"]:not([' + settings.boundAttr + '])');
        overlays = [...document.querySelectorAll(settings.hookOverlays + '[' + settings.typeAttr + '="' + settings.type + '"]:not([' + settings.boundAttr + '])')];
    }

    if (!overlays.length) {
        return false;
    }

    // Bind all eventhandlers
    bindEvents();

    return instance;
};
